import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { supabase } from "@/integrations/supabase/client"
import { toast } from "sonner"

export interface LicenseCompany {
  id: string
  condominium_id: string | null
  legal_name: string
  trade_name: string | null
  document: string | null
  state_registration: string | null
  email: string | null
  phone: string | null
  whatsapp: string | null
  responsible_name: string | null
  responsible_email: string | null
  responsible_phone: string | null
  postal_code: string | null
  street: string | null
  street_number: string | null
  address_complement: string | null
  neighborhood: string | null
  city: string | null
  state: string | null
  notes: string | null
  status: "active" | "suspended" | "archived"
  created_at: string
  updated_at: string
}

export interface LicenseActivation {
  id: string
  machine_id: string
  machine_label: string | null
  app_version: string | null
  first_activated_at: string
  last_seen_at: string
  revoked_at: string | null
}

export interface License {
  id: string
  license_key: string
  key_preview: string | null
  client_name: string
  company_id: string
  company: LicenseCompany
  is_active: boolean
  status: "trial" | "active" | "past_due" | "suspended" | "revoked" | "expired" | "archived"
  license_type: "trial" | "subscription" | "perpetual"
  plan: string
  expiration_date: string | null
  expires_at: string | null
  max_devices: number | null
  max_offline_days: number
  grace_days: number
  last_validated_at: string | null
  validations_count: number
  app_version: string | null
  notes: string | null
  created_at: string
  updated_at: string | null
  condominium_id: string | null
  activations: LicenseActivation[]
}

export interface CompanyInput extends Partial<LicenseCompany> {
  legal_name: string
}

const invoke = async <T,>(body: Record<string, unknown>): Promise<T> => {
  const { data, error } = await supabase.functions.invoke("admin-license", { body })
  if (error) throw new Error(error.message || "Falha ao acessar o servidor de licenças.")
  if (data?.error) throw new Error(data.error)
  return data as T
}

export const useLicenses = () => useQuery({
  queryKey: ["licenses"],
  queryFn: async () => (await invoke<{ licenses: License[] }>({ action: "list" })).licenses,
})

export const useLicenseCompanies = () => useQuery({
  queryKey: ["license-companies"],
  queryFn: async () => (await invoke<{ companies: LicenseCompany[] }>({ action: "list_companies" })).companies,
})

export const useSaveLicenseCompany = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (company: CompanyInput) => invoke<{ company: LicenseCompany }>({ action: "save_company", company }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["license-companies"] })
      toast.success("Empresa salva com sucesso.")
    },
    onError: (error: Error) => toast.error(error.message),
  })
}

export interface CreateLicenseInput {
  companyId: string
  licenseType: "trial" | "subscription" | "perpetual"
  plan: string
  days: number
  maxDevices: number
  maxOfflineDays: number
  graceDays: number
  notes?: string
}

export const useCreateLicense = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateLicenseInput) => invoke<{ key: string; license: License }>({ action: "create", ...input }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["licenses"] })
      queryClient.invalidateQueries({ queryKey: ["license-companies"] })
      toast.success("Licença gerada e vinculada à empresa.")
    },
    onError: (error: Error) => toast.error(error.message),
  })
}

const useLicenseAction = (success: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { action: string; licenseId: string; days?: number; reason?: string }) => invoke({ ...input }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["licenses"] }); toast.success(success) },
    onError: (error: Error) => toast.error(error.message),
  })
}

export const useToggleLicense = () => {
  const mutation = useLicenseAction("Status da licença atualizado.")
  return {
    ...mutation,
    mutate: ({ id, is_active }: { id: string; is_active: boolean }) =>
      mutation.mutate({ action: is_active ? "activate" : "suspend", licenseId: id, reason: is_active ? undefined : "Suspensa pelo SuperAdmin" }),
  }
}

export const useDeleteLicense = () => {
  const mutation = useLicenseAction("Licença arquivada. O histórico foi preservado.")
  return { ...mutation, mutate: (id: string) => mutation.mutate({ action: "archive", licenseId: id, reason: "Arquivada pelo SuperAdmin" }) }
}

export const useRenewLicense = () => useLicenseAction("Licença renovada.")
export const useResetLicenseMachines = () => useLicenseAction("Computadores liberados para uma nova ativação.")

// Mantido apenas para compatibilidade de imports antigos. Chaves reais são criadas no servidor.
export const generateLicenseKey = () => "GERADA-PELO-SERVIDOR"
